/**
 * Who is signed in. Everything that needs a user reads it from here.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '@/lib/api.ts'
import type { Member } from '@/lib/types.ts'

type AuthValue = {
  user: Member | null
  /** True until the initial session check finishes — don't flash a login form before then. */
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (input: { email: string; password: string; displayName: string; inviteCode: string }) => Promise<void>
  logout: () => Promise<void>
  /** Finish a reset. The server signs them in, so we hold the user too. */
  completeReset: (token: string, password: string) => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Member | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api.auth
      .me()
      .then(({ user }) => { if (!cancelled) setUser(user) })
      .catch(() => { if (!cancelled) setUser(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setUser((await api.auth.login(email, password)).user)
  }, [])

  const signup = useCallback<AuthValue['signup']>(async (input) => {
    setUser((await api.auth.signup(input)).user)
  }, [])

  const completeReset = useCallback(async (token: string, password: string) => {
    setUser((await api.auth.reset(token, password)).user)
  }, [])

  const logout = useCallback(async () => {
    await api.auth.logout()
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, completeReset }),
    [user, loading, login, signup, logout, completeReset],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
